import { useCallback, useState } from "react";
import type RealtimeKitClient from "@cloudflare/realtimekit";
import { Volume2, X } from "lucide-react";
import Swal from "sweetalert2";
import { unlockMediaPlaybackInGesture } from "./mobileSpeakerRouting";

type MobileLowAudioBannerProps = {
    meetingClient: RealtimeKitClient | null;
    mobileOS: "ios" | "android" | "other";
    /** Quando true, exibe o banner (qualquer aba da sala no mobile). */
    visible?: boolean;
    /** Observador / só-ouvindo → sessão playback (alto-falante). */
    listenOnly?: boolean;
    /** Mantido na chamada. Fechar o aviso não reabre sozinho. */
    resetKey?: number | string;
};

const DISMISS_KEY = "constela-low-audio-dismissed";

type SpeakerSelf = {
    getSpeakerDevices?: () => Promise<MediaDeviceInfo[]>;
    setSpeakerDevice?: (device: MediaDeviceInfo) => Promise<unknown>;
};

function readDismissed(): boolean {
    try {
        return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
        return false;
    }
}

function defaultSpeaker(devices: MediaDeviceInfo[]): MediaDeviceInfo | null {
    return (
        devices.find((device) => device.deviceId === "default") ??
        devices.find((device) => /default|padr[aã]o/i.test(device.label)) ??
        null
    );
}

const MobileLowAudioBanner: React.FC<MobileLowAudioBannerProps> = ({
    meetingClient,
    mobileOS,
    visible = true,
}) => {
    const [dismissed, setDismissed] = useState(readDismissed);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState<string | null>(null);

    const handleBoost = useCallback(async () => {
        if (!meetingClient || busy) {
            return;
        }

        setBusy(true);
        setStatus(null);
        try {
            const self = meetingClient.self as SpeakerSelf;
            const speakers = (await self.getSpeakerDevices?.()) ?? [];
            const chosen = defaultSpeaker(speakers);
            if (!chosen || !self.setSpeakerDevice) {
                setStatus("Não achei a saída Padrão neste celular.");
                return;
            }
            await self.setSpeakerDevice(chosen);
            setStatus("Som em Padrão, o mesmo de Configurações, Som, Padrão.");
            await Swal.fire({
                toast: true,
                position: "top",
                icon: "success",
                title: "Som em Padrão",
                showConfirmButton: false,
                timer: 3500,
                timerProgressBar: true,
            });
        } catch {
            setStatus("Não consegui mudar para Padrão. Toque de novo.");
        } finally {
            setBusy(false);
        }
    }, [busy, meetingClient]);

    if (
        dismissed ||
        !meetingClient ||
        !visible ||
        (mobileOS !== "ios" && mobileOS !== "android")
    ) {
        return null;
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className="pointer-events-auto absolute bottom-[calc(7.5rem+env(safe-area-inset-bottom,0px))] left-3 right-3 z-[35] mx-auto flex max-w-md items-center gap-2 rounded-xl border border-amber-400/50 bg-slate-900/95 px-3 py-3 text-white shadow-lg backdrop-blur-sm"
        >
            <Volume2 className="h-5 w-5 shrink-0 text-amber-300" aria-hidden />
            <p className="min-w-0 flex-1 text-xs leading-snug text-slate-100">
                {status ?? (
                    <>
                        O som saiu do padrão? Toque em{" "}
                        <strong className="font-semibold text-white">Padrão</strong>
                        , o mesmo de Configurações, Som, Padrão.
                    </>
                )}
            </p>
            <button
                type="button"
                onPointerDown={(event) => {
                    event.stopPropagation();
                    unlockMediaPlaybackInGesture(meetingClient);
                }}
                onClick={(event) => {
                    event.stopPropagation();
                    void handleBoost();
                }}
                disabled={busy}
                className="shrink-0 touch-manipulation rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-slate-900 disabled:opacity-60"
            >
                {busy ? "…" : "Padrão"}
            </button>
            <button
                type="button"
                onClick={() => {
                    setDismissed(true);
                    try {
                        sessionStorage.setItem(DISMISS_KEY, "1");
                    } catch {
                        // ignore
                    }
                }}
                className="shrink-0 rounded p-0.5 text-slate-500 hover:text-slate-300"
                aria-label="Fechar aviso de áudio"
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
};

export default MobileLowAudioBanner;
